import React, { useEffect, useState } from 'react';
import { Dropdown } from './reusableComponents/dropDown';
import { faProductHunt } from '@fortawesome/free-brands-svg-icons';
import { faIcons, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const buildCategoryListAndMap = (
    productDetailsList,
    selectedProductCategory,
    setSelectedProductCategory,
    setSelectedProduct,
    setProductDetailsMap,
    setProductCategoryList
) => {
    const productCategories = [];
    const productDetailsMapData = new Map();
    if (productDetailsList && productDetailsList.length) {
        productDetailsList.forEach((productDetail, index) => {
            productDetailsMapData.set(
                productDetail.productCategory,
                productDetail.products
            );

            const productCategoryData = {
                key: index,
                label: productDetail.productCategory,
                icon: productDetail.icon,
                iconColor: productDetail.icon,
                selected:
                    productDetail.productCategory === selectedProductCategory,
                action: () => {
                    setSelectedProductCategory(productDetail.productCategory);
                    setSelectedProduct(
                        productDetail.products.length
                            ? productDetail.products[0]
                            : null
                    );
                },
            };
            productCategories.push(productCategoryData);
        });
    }

    // Once a category (and product) is selected, there was previously no way
    // back to the "nothing selected" state - surface a clear/reset entry at
    // the top of the list whenever a category is currently selected.
    if (selectedProductCategory) {
        productCategories.unshift({
            key: 'clear-selection',
            label: 'Clear selection',
            icon: faRotateLeft,
            iconColor: null,
            selected: false,
            action: () => {
                setSelectedProductCategory(null);
                setSelectedProduct(null);
            },
        });
    }

    setProductDetailsMap(productDetailsMapData);
    setProductCategoryList(productCategories);
};

export const DropDownContainer = ({
    productDetailsList,
    selectedProductCategory,
    selectedProduct,
    setSelectedProductCategory,
    setSelectedProduct,
    isLoading,
    isSmallScreen,
}) => {
    const [productDetailsMap, setProductDetailsMap] = useState(new Map());
    const [productCategoryList, setProductCategoryList] = useState([]);
    const [productList, setProductList] = useState([]);

    useEffect(() => {
        buildCategoryListAndMap(
            productDetailsList,
            selectedProductCategory,
            setSelectedProductCategory,
            setSelectedProduct,
            setProductDetailsMap,
            setProductCategoryList
        );
    }, [
        productDetailsList,
        selectedProductCategory,
        setSelectedProduct,
        setSelectedProductCategory,
    ]);

    useEffect(() => {
        if (selectedProductCategory) {
            const products = productDetailsMap.get(selectedProductCategory);
            if (products && products.length) {
                const productsData = products.map((product, index) => {
                    return {
                        key: index,
                        label: product,
                        icon: null,
                        iconColor: null,
                        selected: product === selectedProduct,
                        action: () => {
                            setSelectedProduct(product);
                        },
                    };
                });

                // Same reset affordance as the category dropdown - lets the user
                // get back to "nothing selected" from here too, without needing
                // to switch back to the category dropdown first.
                productsData.unshift({
                    key: 'clear-selection',
                    label: 'Clear selection',
                    icon: faRotateLeft,
                    iconColor: null,
                    selected: false,
                    action: () => {
                        setSelectedProductCategory(null);
                        setSelectedProduct(null);
                    },
                });

                setProductList(productsData);
            }
        } else {
            setProductList([]);
        }
    }, [
        selectedProductCategory,
        selectedProduct,
        productDetailsMap,
        setSelectedProduct,
        setSelectedProductCategory,
    ]);

    if (productCategoryList.length < 1) return <></>;

    if (!isSmallScreen) {
        return (
            <div className='flex h-full items-center justify-between'>
                <div className='flex mx-3'>
                    <Dropdown
                        dropDownList={productCategoryList}
                        showButtonText={true}
                        buttonText={
                            selectedProductCategory
                                ? selectedProductCategory
                                : 'Select Category'
                        }
                        buttonTitle={
                            <>
                                <FontAwesomeIcon
                                    icon={faIcons}
                                    className='mr-2'
                                />
                                Product Category
                            </>
                        }
                        listTitleText={'Product Categories'}
                        buttonHoverText={
                            selectedProductCategory
                                ? `Selected Category : ${selectedProductCategory}`
                                : 'Select Category'
                        }
                        buttonClassName={
                            selectedProductCategory
                                ? 'text-texts-tertiary400 min-w-40'
                                : 'text-texts-secondary300 min-w-40'
                        }
                        listClassName={''}
                        disabled={isLoading}
                    />
                </div>
                <div className='flex mx-3'>
                    <Dropdown
                        dropDownList={productList}
                        showButtonText={true}
                        buttonText={
                            selectedProduct ? selectedProduct : 'Select Product'
                        }
                        buttonTitle={
                            <>
                                <FontAwesomeIcon
                                    icon={faProductHunt}
                                    className='mr-2'
                                />
                                Product
                            </>
                        }
                        buttonTitleClass={''}
                        listTitleText={'Products'}
                        buttonHoverText={
                            selectedProduct
                                ? `Selected Product : ${selectedProduct}`
                                : 'Select Product'
                        }
                        buttonClassName={
                            selectedProduct
                                ? 'text-texts-tertiary400 min-w-40'
                                : 'text-texts-secondary300 min-w-40'
                        }
                        listClassName={''}
                        disabled={isLoading || !selectedProductCategory}
                    />
                </div>
            </div>
        );
    } else {
        return (
            <>
                <div className='flex flex-col p-4 border-b'>
                    <div className='mb-3 text-texts-secondary300 font-semibold'>
                        <FontAwesomeIcon icon={faIcons} className='mr-2' />
                        <span>Product Category</span>
                    </div>
                    <Dropdown
                        dropDownList={productCategoryList}
                        showButtonText={true}
                        buttonText={
                            selectedProductCategory
                                ? selectedProductCategory
                                : 'Select Category'
                        }
                        titleText={'Product Categories'}
                        buttonHoverText={
                            selectedProductCategory
                                ? `Selected Category : ${selectedProductCategory}`
                                : 'Select Category'
                        }
                        buttonClassName={
                            selectedProductCategory
                                ? 'text-texts-tertiary400 w-full'
                                : 'text-texts-secondary300 w-full'
                        }
                        listClassName={''}
                        disabled={isLoading}
                        placement={'bottom'}
                        listTitleText='Product Categories'
                    />
                </div>
                <div className='flex flex-col p-4'>
                    <div className='mb-3 text-texts-secondary300 font-semibold'>
                        <FontAwesomeIcon
                            icon={faProductHunt}
                            className='mr-2'
                        />
                        <span>Product</span>
                    </div>
                    <Dropdown
                        dropDownList={productList}
                        showButtonText={true}
                        buttonText={
                            selectedProduct ? selectedProduct : 'Select Product'
                        }
                        titleText={'Products'}
                        buttonHoverText={
                            selectedProduct
                                ? `Selected Product : ${selectedProduct}`
                                : 'Select Product'
                        }
                        buttonClassName={
                            selectedProduct
                                ? 'text-texts-tertiary400'
                                : 'text-texts-secondary300'
                        }
                        listClassName={''}
                        disabled={isLoading || !selectedProductCategory}
                        placement={'bottom'}
                        listTitleText='Products'
                    />
                </div>
            </>
        );
    }
};
